/**
 * Nutrition and BMR/TDEE calculation utilities.
 * Used by DashboardPage and other nutrition UIs.
 */

/**
 * Harris-Benedict BMR (Basal Metabolic Rate).
 * @param {number} age - Age in years
 * @param {string} gender - 'male' | 'female' | 'm' | 'f' | 'other'
 * @param {number} weight_kg - Weight in kg
 * @param {number} height_cm - Height in cm
 * @returns {number|null} BMR in kcal or null if inputs invalid
 */
export function calculateBMR(age, gender, weight_kg, height_cm) {
  if (!age || !gender || !weight_kg || !height_cm) return null;
  const g = String(gender).toLowerCase();
  const w = parseFloat(weight_kg);
  const h = parseFloat(height_cm);
  const a = parseInt(age);
  if (g === 'male' || g === 'm') return 66.5 + (13.75 * w) + (5.003 * h) - (6.75 * a);
  if (g === 'female' || g === 'f') return 655.1 + (9.563 * w) + (1.850 * h) - (4.676 * a);
  return ((66.5 + (13.75 * w) + (5.003 * h) - (6.75 * a)) + (655.1 + (9.563 * w) + (1.850 * h) - (4.676 * a))) / 2;
}

/**
 * Activity level multiplier for TDEE (Mifflin-St.Jeor / common factors).
 * @param {string} activityLevel - sedentary | light | moderate | active | extreme
 * @returns {number}
 */
export function getActivityMultiplier(activityLevel) {
  const multipliers = { sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, extreme: 1.9 };
  return multipliers[activityLevel] || 1.2;
}

/**
 * Goal-based calorie factor (deficit/surplus/maintain).
 * @param {string} goal - lose | cut | maintain | gain | muscle | improve_performance | improve_health
 * @returns {number}
 */
export function getGoalFactor(goal) {
  const factors = { lose: 0.85, cut: 0.80, gain: 1.15, muscle: 1.12, improve_performance: 1.10, improve_health: 1.0, maintain: 1.0 };
  return factors[goal] || 1.0;
}

/**
 * BMI from weight (kg) and height (cm).
 * @param {number} weight - kg
 * @param {number} height - cm
 * @returns {string|null} BMI to 1 decimal or null
 */
export function calculateBMI(weight, height) {
  if (!weight || !height) return null;
  return (weight / ((height / 100) * (height / 100))).toFixed(1);
}
